import {
  Card, Grid, Title, Text, Metric,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@tremor/react';
import type { CustomerAR, CustomerOpenInvoice, CustomerPayment } from '../api/types';
import { t } from '../lib/i18n';

const mxn = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

interface Props {
  ar: CustomerAR;
  openInvoices: CustomerOpenInvoice[];
  recentPayments: CustomerPayment[];
  dsoDays: number | null;
}

export default function CustomerArSection({ ar, openInvoices, recentPayments, dsoDays }: Props) {
  return (
    <div className="space-y-4">
      <Grid numItemsSm={2} numItemsLg={6} className="gap-2">
        <Card>
          <Text>{t.customer.ar.total}</Text>
          <Metric>{mxn(ar.total_mxn)}</Metric>
        </Card>
        {ar.aging.map((b) => {
          // Aging label keys: '0-30' -> 'b0_30', '31-60' -> 'b31_60', '90+' -> 'b90p'.
          const key = 'b' + b.label.replace('-', '_').replace('+', 'p');
          const label = (t.customer.ar as Record<string, string>)[key] ?? b.label;
          return (
            <Card key={b.label}>
              <Text>{label}</Text>
              <Metric>{mxn(b.amount_mxn)}</Metric>
            </Card>
          );
        })}
        <Card>
          <Text>{t.customer.dso.label}</Text>
          <Metric>{dsoDays === null ? t.customer.dso.na : Math.round(dsoDays)}</Metric>
          <Text className="text-xs text-tremor-content">{t.customer.dso.hint}</Text>
        </Card>
      </Grid>

      <Card>
        <Title>{t.customer.openInvoices.title}</Title>
        {openInvoices.length === 0 ? (
          <Text className="mt-2">{t.customer.openInvoices.empty}</Text>
        ) : (
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.customer.openInvoices.cols.idate}</TableHeaderCell>
                <TableHeaderCell>{t.customer.openInvoices.cols.inum}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.openInvoices.cols.total}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.openInvoices.cols.balance}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.openInvoices.cols.daysOld}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {openInvoices.map((i) => (
                <TableRow key={`${i.sid}-${i.tnum}`}>
                  <TableCell>{i.idate}</TableCell>
                  <TableCell>{i.inum}</TableCell>
                  <TableCell className="text-right">{mxn(i.total_mxn)}</TableCell>
                  <TableCell className="text-right">{mxn(i.balance_mxn)}</TableCell>
                  <TableCell className="text-right">{i.days_old}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <Title>{t.customer.recentPayments.title}</Title>
        {recentPayments.length === 0 ? (
          <Text className="mt-2">{t.customer.recentPayments.empty}</Text>
        ) : (
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.customer.recentPayments.cols.tdate}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.customer.recentPayments.cols.amount}</TableHeaderCell>
                <TableHeaderCell>{t.customer.recentPayments.cols.appliedTo}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentPayments.map((p, idx) => (
                <TableRow key={idx}>
                  <TableCell>{p.tdate}</TableCell>
                  <TableCell className="text-right">{mxn(p.amount_mxn)}</TableCell>
                  <TableCell>{p.applied_to_inum ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
